import { Fragment, useState } from 'react';
import { ChevronRight, Download, ExternalLink, Receipt } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  OrgAdminEmptyState,
  OrgAdminErrorState,
  OrgAdminTableSkeleton,
} from '@/components/molecules/org-admin/OrgAdminStates';
import { PlainHeader } from '@/components/molecules/org-admin/OrgAdminTable';
import { useOrgInvoices } from '@/hooks/org-admin/useBilling';
import type { OrgInvoice } from '@/services/org-admin';
import { formatCurrencyUsd } from './usageFormat';

/**
 * The org's Stripe invoices, with their line items and both download routes.
 *
 * Two things this deliberately does NOT do:
 *
 *   - **It does not proxy the documents.** `invoice_pdf` and `hosted_invoice_url` are Stripe-hosted
 *     links carrying their own signed access, so they are opened directly. Streaming them through
 *     our backend would mean holding customer billing documents we have no reason to hold, and the
 *     links already expire on Stripe's schedule.
 *   - **It does not render its own money formatting.** Amounts arrive as dollars — the backend
 *     converts Stripe's minor units once, at the client boundary — so this uses the same
 *     `formatCurrencyUsd` as every other figure in the module.
 *
 * Line items sit in a disclosure row rather than always-on, using the module's existing
 * `oa-subtable` pattern (see `MemberUsageTable`). Most invoices here are a single line; the ones
 * worth expanding are plan changes, which produce a prorated credit line and a prorated charge line
 * whose amounts only make sense read together.
 */

export interface InvoicesPanelProps {
  /** Non-admins never see this panel; the endpoint 403s them anyway. */
  enabled: boolean;
}

const DATE = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? '—' : DATE.format(new Date(parsed));
}

/**
 * Stripe's status vocabulary, mapped to the module's tone language.
 *
 * `open` is a live obligation and `uncollectible` is a failure, so both are warned. `draft` and
 * `void` are neither good nor bad — they are states an invoice passes through — so they stay
 * neutral rather than borrowing a colour that implies a verdict.
 */
const STATUS_TONE: Record<string, 'ok' | 'warn' | 'neutral'> = {
  paid: 'ok',
  open: 'warn',
  uncollectible: 'warn',
  draft: 'neutral',
  void: 'neutral',
};

function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONE[status] ?? 'neutral';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-1.5 py-0.5 text-[11px] font-medium capitalize',
        tone === 'ok' && 'bg-success/10 text-success-strong',
        tone === 'warn' && 'bg-warning/10 text-warning-strong',
        tone === 'neutral' && 'bg-muted text-muted-foreground',
      )}
    >
      {status}
    </span>
  );
}

/** The period a prorated line covers — what makes a part-month amount read as deliberate. */
function linePeriod(line: OrgInvoice['lines'][number]): string | null {
  if (!line.period_start || !line.period_end) return null;
  return `${formatDate(line.period_start)} – ${formatDate(line.period_end)}`;
}

export function InvoicesPanel({ enabled }: InvoicesPanelProps) {
  const { data, isLoading, isError, error, refetch } = useOrgInvoices({ enabled });
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section className="oa-panel" aria-labelledby="oa-invoices-title">
      <header className="oa-toolbar py-2.5">
        <h3 id="oa-invoices-title" className="oa-title truncate">
          Invoices
        </h3>
        <p className="oa-meta mt-0.5">
          Every charge Stripe has issued for this organization. Open or download the receipt.
        </p>
      </header>

      {isLoading ? (
        <OrgAdminTableSkeleton
          columns={['Date', 'Invoice', 'Status', 'Total']}
          label="Loading invoices"
        />
      ) : isError ? (
        <OrgAdminErrorState
          title="Couldn't load invoices"
          error={error}
          onRetry={refetch}
          className="min-h-[160px]"
        />
      ) : !data?.length ? (
        <OrgAdminEmptyState
          icon={Receipt}
          title="No invoices yet"
          description="Invoices appear here once this organization has been charged for a plan or a top-up."
          className="min-h-[160px]"
        />
      ) : (
        <table className="oa-table">
          <caption className="sr-only">Stripe invoices for this organization, newest first</caption>
          <thead>
            <tr>
              <PlainHeader label="Expand" srOnly width="36px" />
              <PlainHeader label="Date" />
              <PlainHeader label="Invoice" />
              <PlainHeader label="Status" />
              <PlainHeader label="Total" align="right" />
              <PlainHeader label="Download" srOnly align="right" width="88px" />
            </tr>
          </thead>
          <tbody>
            {data.map((invoice) => {
              const isExpanded = expandedIds.has(invoice.id);
              const canExpand = invoice.lines.length > 0;
              // A draft has no number until Stripe finalises it, so the id is the only stable
              // handle — shown rather than an empty cell so every row can be referred to.
              const label = invoice.number ?? invoice.id;

              return (
                <Fragment key={invoice.id}>
                  <tr>
                    <td>
                      {canExpand && (
                        <button
                          type="button"
                          aria-expanded={isExpanded}
                          aria-controls={`invoice-lines-${invoice.id}`}
                          aria-label={`${isExpanded ? 'Hide' : 'Show'} line items for invoice ${label}`}
                          onClick={() => toggleExpanded(invoice.id)}
                          className="grid h-6 w-6 place-items-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                          <ChevronRight
                            className={cn(
                              'h-3.5 w-3.5 transition-transform duration-150 motion-reduce:transition-none',
                              isExpanded && 'rotate-90',
                            )}
                            aria-hidden
                          />
                        </button>
                      )}
                    </td>
                    <td className="truncate">{formatDate(invoice.created)}</td>
                    <td className="truncate font-medium">{label}</td>
                    <td>
                      <StatusBadge status={invoice.status} />
                    </td>
                    <td className="oa-num">
                      {invoice.total === null ? '—' : formatCurrencyUsd(invoice.total)}
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-1">
                        {/*
                          `rel="noreferrer"` alongside `target="_blank"`: these links leave for
                          Stripe, and the opened page must not get a handle back on this one.
                        */}
                        {invoice.hosted_invoice_url && (
                          <a
                            href={invoice.hosted_invoice_url}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={`Open invoice ${label} on Stripe`}
                            className="oa-interactive grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                          >
                            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                          </a>
                        )}
                        {invoice.invoice_pdf && (
                          <a
                            href={invoice.invoice_pdf}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={`Download invoice ${label} as PDF`}
                            className="oa-interactive grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                          >
                            <Download className="h-3.5 w-3.5" aria-hidden />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr>
                      <td colSpan={6} className="!p-0">
                        <div
                          id={`invoice-lines-${invoice.id}`}
                          className="oa-subtable-shell px-[var(--oa-gutter)] py-2"
                        >
                          <table className="oa-table oa-subtable">
                            <caption className="sr-only">Line items for invoice {label}</caption>
                            <thead>
                              <tr>
                                <PlainHeader label="Description" />
                                <PlainHeader label="Period" />
                                <PlainHeader label="Qty" align="right" width="60px" />
                                <PlainHeader label="Amount" align="right" width="100px" />
                              </tr>
                            </thead>
                            <tbody>
                              {invoice.lines.map((line) => (
                                <tr key={line.id}>
                                  <td className="truncate">{line.description ?? '—'}</td>
                                  <td className="truncate text-muted-foreground">
                                    {linePeriod(line) ?? '—'}
                                  </td>
                                  <td className="oa-num">{line.quantity ?? '—'}</td>
                                  <td className="oa-num">
                                    {line.amount === null ? '—' : formatCurrencyUsd(line.amount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
